<template>
  <RevCard class="p-24">
    <h2 class="heading-2 mb-16">
      {{ i18n(translations.title) }}
    </h2>
    <WithSkeletonLoader :is-loading="pending || addressFieldValidatorPending">
      <div class="body-1-bold flex h-[24px] items-center">
        <div class="w-32">
          <IconFactory />
        </div>
        {{ businessDetails.company }}
      </div>
      <div class="body-1 flex h-[24px] items-center">
        <div class="ml-32">
          {{
            i18n(translations.fullName, {
              firstName: businessDetails.firstName,
              lastName: businessDetails.lastName,
            })
          }}
        </div>
      </div>
      <div
        v-if="shouldDisplayNamePronunciation"
        class="body-1 flex h-[24px] items-center"
      >
        <div class="ml-32">
          {{
            i18n(translations.fullName, {
              firstName: businessDetails.firstNamePronunciation,
              lastName: businessDetails.lastNamePronunciation,
            })
          }}
        </div>
      </div>
      <div class="body-1 flex h-[24px] items-center">
        <div class="ml-32">{{ businessDetails.street }}</div>
      </div>
      <div class="body-1 flex h-[24px] items-center">
        <div class="ml-32">
          {{ businessDetails.postalCode }} {{ businessDetails.city }}
        </div>
      </div>
      <div
        v-if="businessDetails.stateOrProvince"
        class="body-1 flex h-[24px] items-center"
      >
        <div class="ml-32">{{ businessDetails.stateOrProvince }}</div>
      </div>
      <div
        v-if="businessDetails.country"
        class="body-1 flex h-[24px] items-center"
      >
        <div class="ml-32">{{ i18n.country(businessDetails.country) }}</div>
      </div>
      <div class="body-1 mb-16 flex h-[24px] items-center">
        <div class="w-32">
          <IconSmartphone />
        </div>
        {{ businessDetails.phone }}
      </div>
      <RevButton
        id="open_edit_info_modal_button"
        full-width="never"
        :icon="IconEdit"
        size="small"
        variant="secondary"
        @click="handleOpenModal"
      >
        {{ i18n(translations.editButton) }}
      </RevButton>
      <RevDrawer :name="MODAL_NAME" :title="i18n(translations.modalTitle)">
        <template #body="{ close }">
          <RevInfoBlock
            class="mb-12"
            :content="i18n(translations.modalInfoBlockContent)"
            :icon="IconLightbulb"
            :title="i18n(translations.modalInfoBlockTitle)"
            variant="warning"
          />
          <RevInfoBlock
            v-if="apiError"
            class="mb-4"
            :icon="IconWarning"
            :title="i18n(translations.errorTitle)"
            variant="danger"
          >
          </RevInfoBlock>
          <RevForm
            class="mb-32"
            :initial-values="businessDetails"
            :validate="validate()"
            @submit="handleSubmit"
          >
            <template #default="{ values, errors, hasError }">
              <DynamicAddressFieldValidatorsErrorLogger
                :errors
                :form-values="values"
                logger-scope="COMPANY_INFO"
              />
              <AddressFields
                v-model:city="values.city"
                v-model:company="values.company"
                v-model:country="values.country"
                v-model:first-name="values.firstName"
                v-model:first-name-pronunciation="values.firstNamePronunciation"
                v-model:last-name="values.lastName"
                v-model:last-name-pronunciation="values.lastNamePronunciation"
                v-model:phone="values.phone"
                v-model:postal-code="values.postalCode"
                v-model:public-company-name="values.publicCompanyName"
                v-model:state-or-province="values.stateOrProvince"
                v-model:street="values.street"
                class="mb-16"
                :company-field-label-message="i18n(translations.companyLabel)"
                :default-country="values.defaultCountry"
                :errors
                :feature-code="FeatureCode.WEB_COMPANY_INFO_PROFILE"
                :optional-fields="[]"
              />
              <RevButton
                id="edit_business_details_button"
                class="mt-8"
                :disabled="hasError || isSubmitting"
                full-width="always"
                :loading="isSubmitting"
                type="submit"
                variant="secondary"
              >
                {{ i18n(translations.submitButton) }}
              </RevButton>
              <RevLink
                id="cancel_form"
                class="m-auto mt-12 w-full"
                @click="close"
              >
                {{ i18n(translations.cancelButton) }}
              </RevLink>
            </template>
          </RevForm>
        </template>
      </RevDrawer>
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import type { AddressFieldsValues } from '@backmarket/http-api/src/api-specs-shipping/address/address-validation.types'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { AddressFieldsValidators } from '@backmarket/nuxt-module-address/AddressFieldsValidators'
import DynamicAddressFieldValidatorsErrorLogger from '@backmarket/nuxt-module-address/DynamicAddressFieldValidatorsErrorLogger.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useDynamicAddressFieldValidators } from '@backmarket/nuxt-module-address/useDynamicAddressFieldValidators'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { omit } from '@backmarket/utils/object/omit'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDrawer } from '@ds/components/Drawer'
import {
  RevForm,
  type ValidatorDeclaration,
  makeValidate,
  required,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { IconEdit } from '@ds/icons/IconEdit'
import { IconFactory } from '@ds/icons/IconFactory'
import { IconLightbulb } from '@ds/icons/IconLightbulb'
import { IconSmartphone } from '@ds/icons/IconSmartphone'
import { IconWarning } from '@ds/icons/IconWarning'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { MODAL_NAMES } from '../../Profile.constants'
import WithSkeletonLoader from '../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './CompanyInfo.translations'
import type { FormValues } from './CompanyInfo.types'

const MODAL_NAME = MODAL_NAMES.EDIT_BUSINESS_DETAILS

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()
const { isFeatureEnabled } = useBusinessFeaturesStore()
const isKanaNameEnabled = !!isFeatureEnabled('kanaNameEnabled')
const { getDynamicAddressFieldValidatorsWithAsyncData } =
  useDynamicAddressFieldValidators()

const INITIAL_FORM_VALUES: FormValues = {
  firstName: '',
  lastName: '',
  company: '',
  city: '',
  postalCode: '',
  country: '',
  stateOrProvince: '',
  street: '',
  phone: '',
  publicCompanyName: '',
  ...insertIf(isKanaNameEnabled, {
    firstNamePronunciation: '',
    lastNamePronunciation: '',
  }),
}

const {
  data: businessDetails,
  error,
  pending,
  refresh,
} = useHttpFetch(sellerProfileAPI.getProfileViewAddress, {
  server: false,
  queryParams: { views: 'address' },
  default: () => INITIAL_FORM_VALUES,
  transform: (data): FormValues => ({
    firstName: data.address.firstName,
    lastName: data.address.lastName,
    company: data.address.company || '',
    city: data.address.city,
    postalCode: data.address.postalCode,
    country: data.address.country,
    // TODO: Could be better to use stateOrProvince instead of state
    stateOrProvince: data.address.state,
    // TODO: Why are we using addressLine instead of street ?
    street: data.address.addressLine,
    // TODO: Could be better to use phone instead of phoneNumber
    phone: data.address.phoneNumber,
    publicCompanyName: data.company || '',
    ...insertIf(isKanaNameEnabled, {
      firstNamePronunciation: data.address.firstNamePronunciation,
      lastNamePronunciation: data.address.lastNamePronunciation,
    }),
  }),
})

const { data: addressValidation, pending: addressFieldValidatorPending } =
  getDynamicAddressFieldValidatorsWithAsyncData({
    featureFlag: 'FF_ENABLE_ADDRESS_FIELD_VALIDATION_COMPANY_INFO',
    initialFormValues: INITIAL_FORM_VALUES,
    fallbackValidators: {
      ...AddressFieldsValidators(i18n),
      company: {
        ...AddressFieldsValidators(i18n).company,
        required: required(i18n(translations.companyRequired)),
      },
    },
    metadata: {
      scope: 'seller',
    },
  })

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const apiError = ref('')
const isSubmitting = ref(false)

const shouldDisplayNamePronunciation = computed(
  () =>
    isKanaNameEnabled &&
    businessDetails.value &&
    (businessDetails.value.firstNamePronunciation ||
      businessDetails.value.lastNamePronunciation),
)

function validate() {
  // We can safely cast the addressValidation.value as we print the form only when the addressValidation is available
  return makeValidate<FormValues>(
    addressValidation.value as ValidatorDeclaration<AddressFieldsValues>,
  )
}

function handleOpenModal() {
  openModal(MODAL_NAME)
}

async function handleSubmit(profile: FormValues) {
  apiError.value = ''
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateProfile, {
      body: {
        address: {
          // We replace the stateOrProvince and street fields by the state and addressLine fields as the API expects
          ...omit(profile, [
            'phone',
            'stateOrProvince',
            'street',
            'defaultCountry',
            'publicCompanyName',
          ]),
          phoneNumber: profile.phone,
          state: profile.stateOrProvince,
          addressLine: profile.street,
        },
        company: profile.publicCompanyName,
      },
    })
    refresh()
    openSuccessToast({
      title: i18n(translations.addressUpdatedToastSuccessTitle),
      content: i18n(translations.addressUpdatedToastSuccessContent),
    })
    closeModal(MODAL_NAME)
  } catch {
    apiError.value = getApiErrorMessage(error as unknown as Error)
  } finally {
    isSubmitting.value = false
  }
}
</script>
